import Vue from 'vue';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

import { DiscoveryModule, ObjectType, Offer as OfferGraphql } from '@/@types/graphql-types';
import { BuyboxOffer, BuyboxOfferCategory } from '@/interfaces/buybox';
import { Module as ModuleInterface } from '@/interfaces/modules';
import { ClickoutContentType } from '@/interfaces/snowplow/clickout-context';
import {
	Backdrop,
	Clip,
	Credit,
	Episode,
	Offer,
	OfferMonetizationType,
	PromotedBundles,
	Score,
	Season,
	Title,
	TitleObjectType,
	TitleResponse,
	TitleUpcomingData,
} from '@/interfaces/titles';

import { releaseDateCounter } from '@/helpers/date-helper';
import { EntityHelper } from '@/helpers/entity-helper';
import { OfferHelper, OfferLocation } from '@/helpers/offer-helper';
import { PromotedBuyBoxHelper } from '@/helpers/promoted-buybox-helper';
import TitleHelper from '@/helpers/title-helper';
import {
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
	SnowplowTitleContextGraphqlPayload,
} from '@/helpers/tracking/providers/snowplow-contexts';

const INITIAL_STATE = {
	// need to move async-ness into state due to SSR
	_titleLoadedPromise: null as Promise<any> | null,
	_titleLoadedResolve: null as Function | null,
	newestEpisodes: null as Episode[] | null,
	season: null as Title | null,
	title: null as TitleResponse | null,
	similarTitles: [] as Title[],
	upcomingTitles: [] as Title[],
	moduleContext: null as SnowplowModuleContext | null,
	titleContext: null as SnowplowTitleContextGraphql | null,
};

export type TitleDetailState = typeof INITIAL_STATE;

type LoadTitlePayload = {
	objectId: number;
	objectType: TitleObjectType;
	locale: string;
	noSimilarTitles: boolean;
	seasonEpisodeLimit?: number;
};

const state = () => {
	return {
		...INITIAL_STATE,
		_titleLoadedPromise: Promise.resolve(), // initialize with an already resolved promise
	};
};

type State = ReturnType<typeof state>;

const getters: GetterTree<State, any> = {
	title(state): Title | undefined {
		const title = state.title;
		if (!title) {
			return undefined;
		}

		return {
			...title,
			seasons: title.seasons || [],
			episodes: title.episodes || [],
		};
	},

	similarTitles(state): Title[] {
		return state.similarTitles;
	},

	similarUpcomingTitles(state): Title[] {
		return state.upcomingTitles;
	},

	credits(state): Credit[] {
		// return the season’s credits or the title’s credits when first one is empty
		if (state.season && state.season.credits && state.season.credits.length) {
			return state.season.credits;
		}
		if (state.title) {
			return state.title.credits || [];
		}
		return [];
	},

	releaseDate(state, getters, rootGetters): Date | null {
		const title = getters.title;
		const urlDataReleaseDate = rootGetters['routing/urlMetaDataReleaseDate'];
		if (!title) return null;
		const releaseDate = title.cinema_release_date || title.release_date || urlDataReleaseDate;
		return new Date(releaseDate);
	},

	isReleaseDateInFuture(state, getters): boolean {
		const releaseDate = getters.releaseDate;
		return releaseDate > new Date();
	},

	season(state): Title | undefined {
		return state.season || undefined;
	},

	newestEpisodes(state): Episode[] {
		return state.newestEpisodes ?? [];
	},

	offers(state, getters, rootGetters): Offer[] {
		if (!rootGetters.routing.urlMetadata) {
			return [];
		}

		const objectType = rootGetters.routing.urlMetadata.object_type;
		if (objectType === 'show_season') {
			if (state.season?.offers) {
				return state.season.offers;
			}
		} else {
			if (state.title?.offers) {
				return state.title.offers;
			}
		}

		return [];
	},

	isTitleUpcoming(state, getters) {
		const hasOffers = OfferHelper.hasStreamingOffers(getters.offers) || getters.hasAnyOffers;
		return !hasOffers && getters.availableUpcoming.length > 0;
	},

	/**
	 * Get all upcoming details for the *current* title.
	 * Upcoming info is stored differently for movies, shows, or episodes, this gets you
	 * the correct info.
	 *
	 * if you need only upcoming with offers actually available (country, providers, etc.)
	 * look at `@TitleDetailsStore.Getter availableUpcoming`.
	 */
	upcomingDetails(state, getters, rootState, rootGetters): TitleUpcomingData[] {
		if (rootGetters['routing/isSeasonPage']) {
			return (
				state.season?.upcoming ??
				// Additionally show upcoming details for *new* seasons, ie when only episode 1 is upcoming
				state.season?.episodes?.[0]?.upcoming ??
				[]
			);
		}

		// For show pages, the upcoming info might only be on the seasons and not the title.
		// This maps through all seasons but I expect only the last would have upcoming info with it
		return state.title?.upcoming ?? state.title?.seasons?.map(season => season.upcoming ?? []).flat() ?? [];
	},

	/**
	 * Filters a title's upcoming info to the entries actually available based
	 * on user and app state (country, language, providers, etc).
	 */
	availableUpcoming(state, getters, rootState, rootGetters) {
		const providersById = rootGetters['constant/providersById'];

		const available = (<TitleUpcomingData[]>getters.upcomingDetails).filter(
			({ package_id }) => String(package_id) in providersById || package_id === 0
		);

		// get a unique (by provider id) list of upcoming info
		const duplicates = new Set<number>();
		const uniqueAvailable: TitleUpcomingData[] = [];
		for (const info of available) {
			if (duplicates.has(info.package_id)) continue;

			uniqueAvailable.push(info);
			duplicates.add(info.package_id);
		}

		// if there are no available offers (due to country, provider, etc), but some info does exist, show just one
		// this lets us show some interactive content rather than nothing
		const allOffers =
			uniqueAvailable.length > 0 ? uniqueAvailable : (<TitleUpcomingData[]>getters.upcomingDetails).slice(0, 1);

		// if we do have some offers that are complete (date || provider), it's better to filter out the incomplete ones
		const completeOffers = allOffers.filter(
			i => i.package_id !== 0 && releaseDateCounter(new Date(i.release_window_from)) != null
		);

		return completeOffers.length > 0 ? completeOffers : allOffers;
	},

	promotedBundleOffers(state): Offer[] {
		if (state.title?.promoted_bundles) {
			return state.title.promoted_bundles.map((bundle: PromotedBundles) => {
				// BUYBOX_BUNDLE: Transform promoted bundle to offer format
				return {
					provider_id: bundle.offer.provider_id,
					presentation_type: bundle.offer.presentation_type,
					package_short_name: bundle.short_name,
					monetization_type: OfferMonetizationType.BUNDLE,
					// BUYBOX_BUNDLE: make use of 'bundle' monetization_type, so we can correctly show
					// the promoted_bundle in the buybox.
					urls: {
						standard_web: bundle.promotion_url,
					},
					country: bundle.offer.country,
					currency: bundle.offer.currency,
					jw_entity_id: bundle.offer.jw_entity_id,
					bundle_id: bundle.id,
					bundle_technical_name: bundle.technical_name,
					bundle_icon_url: bundle.icon_url,
				};
			});
		}
		return [];
	},

	hasBundleOffers(state, getters): boolean {
		return getters.promotedBundleOffers.length > 0;
	},

	promotedOffers(state, getters, rootState): { offers: Offer[]; categories: BuyboxOfferCategory[] } {
		const { categories, offers: offerIDs } = PromotedBuyBoxHelper.getPromotedOffers(
			rootState.language.webLocale,
			getters.offers
		);
		let offers = getters.offers.filter((offer: Offer) => offerIDs.includes(offer.provider_id));

		return {
			offers,
			categories,
		};
	},

	orderedEpisodes(state, getters): Episode[] {
		// @TODO clean up where the episodes come from
		const season: Season = getters.season;
		if (season?.episodes == null) return [];

		const episodes = [...season.episodes];
		// ascending episode number order
		episodes.sort((a, b) => a.episode_number - b.episode_number);

		return episodes;
	},

	orderedSeasons(state, getters): Season[] {
		const title: Title = getters.title;
		if (title?.seasons == null) return [];

		const seasons = [...title.seasons];
		// descending season number order
		seasons.sort((a, b) => b.season_number - a.season_number);

		return seasons.map(season => ({ ...season, show_id: season.show_id ?? title.id }));
	},

	synopsis(state): string {
		if (state.season) {
			return state.season.short_description;
		}
		if (state.title) {
			return state.title.short_description;
		}
		return '';
	},

	originalSynopsis(state): string {
		if (state.season) {
			return state.season.original_short_description;
		}
		if (state.title) {
			return state.title.original_short_description;
		}
		return '';
	},

	clips(state, getters, rootState, rootGetters): Clip[] {
		// contentDataClips are more important than the title’s clip
		const contentDataClips = rootGetters['routing/urlMetaDataClips'];
		if (contentDataClips.length > 0) return contentDataClips;

		// when the season is defined use the season’s clips prior
		if (getters.season?.clips?.length > 0) return getters.season.clips;

		return getters.title?.clips ?? [];
	},

	tmdbId(state, getters): number {
		return getters.scoringsByType['tmdb:id'] || 0;
	},

	scoringsByType(state, getters): Record<string, number> {
		const scorings = (getters.title || {}).scoring || [];
		return scorings.reduce((acc: Record<string, number>, cur: Score) => {
			acc[cur.provider_type] = cur.value;
			return acc;
		}, {});
	},

	backdrops(state, getters): Backdrop[] {
		return (getters.title || {}).backdrops || [];
	},

	backdropImageUrls(state, getters): string[] {
		const pathExtension = getters.pathExtension;
		return getters.backdrops.map((backdrop: Backdrop) => {
			return backdrop.backdrop_url + pathExtension;
		});
	},

	// For SEO
	pathExtension(state, getters) {
		const title = getters.title;
		if (!title || !title.full_path) {
			return '';
		}
		return '/' + title.full_path.substr(title.full_path.lastIndexOf('/') + 1);
	},

	hasStreamingOffers(_, getters): boolean {
		return getters.title && OfferHelper.hasStreamingOffers(getters.offers);
	},

	hasFlatrateOffers(_, getters): boolean {
		return getters.title && OfferHelper.hasFlatrateOffers(getters.offers);
	},

	/** Whether the title has streaming or flatrate offers */
	hasAnyOffers(_, { hasStreamingOffers, hasFlatrateOffers }): boolean {
		return hasStreamingOffers || hasFlatrateOffers;
	},

	hasPhysicalOffers(state, getters): boolean | undefined {
		return getters.title
			? getters.offers.filter((offer: Offer) => ['bluray', 'dvd'].indexOf(offer.presentation_type) !== -1)
					.length > 0
			: undefined;
	},

	refferalModuleContext(state): SnowplowModuleContext | null {
		return state.moduleContext;
	},
	titleContext(state): SnowplowTitleContextGraphql | null {
		return state.titleContext;
	},
	// this only applies to the very first detail title load promise, not subsequent title loads
	whenDetailTitleLoaded(state): Promise<any> {
		return state._titleLoadedPromise;
	},
};

const actions: ActionTree<State, any> = {
	async loadTitle({ commit, dispatch, getters }, payload: LoadTitlePayload) {
		// Broken down by object type to ensure all data fetching is done together before being saved
		// in Vuex. This improves TitleDetail page CLS since data required by different parts of the
		// page is available at the same time.
		switch (payload.objectType) {
			case TitleObjectType.MOVIE:
				await dispatch('loadMovieTitle', payload);
				break;
			case TitleObjectType.SHOW:
				await dispatch('loadShowTitle', payload);
				break;
			case TitleObjectType.SEASON:
				await dispatch('loadSeasonTitle', payload);
				break;
			default:
				throw new Error(`Unable to handle title object type ${payload.objectType}`);
		}

		dispatch('meta/setJsonLD', { title: getters.title }, { root: true });

		if (!payload.noSimilarTitles) await dispatch('loadTitleRecommendations');

		// tell the server (and other components) that the title has been fully loaded
		commit('RESOLVE_TITLE_LOADED_PROMISE');
	},

	async loadMovieTitle({ commit, rootGetters }, { objectId, objectType, locale }: LoadTitlePayload) {
		const movieData = await TitleHelper.fetchTitle(locale, objectType, objectId, rootGetters['language/language']);
		commit('SET_TITLE', movieData);
	},

	async loadShowTitle({ commit, rootGetters }, { objectId, objectType, locale }: LoadTitlePayload) {
		const language = rootGetters['language/language'];
		const showData = await TitleHelper.fetchTitle(locale, objectType, objectId, language);

		const showId = showData.show_id ?? showData.id;
		const newestEpisodesResponse = await TitleHelper.fetchEpisodes(locale, showId, {
			page_size: 3,
			language,
			fields: ['episode.show_id'],
		});

		commit('SET_TITLE', showData);
		commit('SET_NEWEST_EPISODES', newestEpisodesResponse.items);
	},

	async loadSeasonTitle({ commit, rootGetters }, payload: LoadTitlePayload) {
		const { objectId, objectType, locale, seasonEpisodeLimit = 20 } = payload;
		const language = rootGetters['language/language'];

		const seasonData = await TitleHelper.fetchTitle(locale, objectType, objectId, language);
		const showData = await TitleHelper.fetchTitle(locale, TitleObjectType.SHOW, <any>seasonData?.show_id, language);

		const showId = showData.show_id ?? showData.id;
		const newestEpisodesResponse = await TitleHelper.fetchEpisodes(locale, showId, {
			page_size: 3,
			language,
			fields: ['episode.show_id'],
		});

		const { episodes = [] } = showData;
		showData.episodes = episodes.slice(0, seasonEpisodeLimit);
		showData.episode_number = episodes?.length;

		commit('SET_TITLE', showData);
		commit('SET_SEASON', seasonData);
		commit('SET_NEWEST_EPISODES', newestEpisodesResponse.items);
	},

	async loadTitleRecommendations({ commit, getters, rootGetters }) {
		const objectId = getters.title.id;
		const objectType = getters.title.object_type;

		// Only fetch similar titles for supported types.
		if (![TitleObjectType.MOVIE, TitleObjectType.SHOW, TitleObjectType.SEASON].includes(objectType)) return;

		const locale = rootGetters['language/locale'];
		const language = rootGetters['language/language'];

		let similarObjectType = objectType;
		let similarObjectId = objectId;
		if (objectType === TitleObjectType.SEASON) {
			similarObjectType = TitleObjectType.SHOW;
			similarObjectId = getters.season?.show_id ?? getters.title.id;
		}

		const titleDetailExperimentsVariants = rootGetters['experiment/parseMLExperimentsVariants']('titleDetail');

		const genreIds = getters.title.genre_ids ?? [];
		const genresById = rootGetters['constant/genresById'];
		const genres = genreIds.map((id: string) => genresById[id].short_name);

		// only query similar upcoming titles when it is not an upcoming title
		const similarUpcomingTitlesPromise: ReturnType<typeof TitleHelper.getSimilarUpcomingTitles> =
			getters.isTitleUpcoming
				? Promise.resolve({ items: [], total_results: 0 })
				: TitleHelper.getSimilarUpcomingTitles({ locale, genres, contentType: objectType });
		const [similarUpcomingTitles, similarTitles] = await Promise.all([
			similarUpcomingTitlesPromise,
			TitleHelper.getSimilarTitles({
				locale,
				language,
				entityIds: [EntityHelper.getEntityId(similarObjectType, similarObjectId)],
				titleDetailExperimentsVariants,
			}),
		]);

		commit('SET_SIMILAR_TITLES', similarTitles.items);

		// for similar upcoming filter out current title from recommendations
		commit(
			'SET_SIMILAR_UPCOMING_TITLES',
			(similarUpcomingTitles.items || []).filter(({ id }) => id !== objectId)
		);
	},

	trackClickOut(
		{ rootState },
		payload: {
			offer: BuyboxOffer;
			offerLocation?: OfferLocation;
			objectType: TitleObjectType;
			objectId: number;
			episodeNumber?: number;
			seasonNumber?: number;
			additionalContexts?: SnowplowContext[];
		}
	) {
		const { offer, offerLocation, episodeNumber, seasonNumber, objectType, objectId, additionalContexts } = payload;

		OfferHelper.trackClickOut(
			rootState.user.jwId,
			offer.offer,
			offer.provider,
			objectType,
			objectId,
			seasonNumber,
			episodeNumber,
			offerLocation,
			additionalContexts
		);
	},
	trackClickOutGraphql(
		{ rootState },
		payload: {
			offer: OfferGraphql;
			offerLocation?: OfferLocation;
			objectType: ObjectType;
			objectId: number;
			episodeNumber?: number;
			seasonNumber?: number;
			additionalContexts?: SnowplowContext[];
			contentType?: ClickoutContentType.LeavingSoon;
		}
	) {
		const {
			offer,
			offerLocation,
			episodeNumber,
			seasonNumber,
			objectType,
			objectId,
			additionalContexts,
			contentType,
		} = payload;

		OfferHelper.trackClickOutGraphql(
			rootState.user.jwId,
			offer,
			objectType,
			objectId,
			seasonNumber,
			episodeNumber,
			offerLocation,
			additionalContexts,
			contentType
		);
	},
	setModuleContext({ commit }, { module, moduleIndex }: { module: ModuleInterface; moduleIndex: number }) {
		commit('SET_MODULE_CONTEXT', { module, moduleIndex });
	},
	clearModuleContext({ commit }) {
		commit('CLEAR_MODULE_CONTEXT');
	},
	setTitleContext({ commit }, payload: SnowplowTitleContextGraphqlPayload) {
		commit('SET_TITLE_CONTEXT', payload);
	},
	clearTitleContext({ commit }) {
		commit('CLEAR_TITLE_CONTEXT');
	},
	resetTitle({ commit }, key: (keyof TitleDetailState)[]) {
		commit('RESET_TITLE', key);
		// HACK Reset nullifies title loaded promise/resolve,
		// which makes it impossible to know when title is loaded after page navigation
		// which in turn screws up context gathering on page load.
		// So we have to refresh it right after reset.
		// Looks to be a very brittle approach for reactive loading.
		commit('INIT_TITLE_LOADED_PROMISE');
	},
};

const mutations: MutationTree<State> = {
	RESET_TITLE(state, keys = Object.keys(state)) {
		(keys as (keyof State)[]).forEach(key => {
			Vue.set(state, key, INITIAL_STATE[key as keyof State]);
		});
	},
	SET_TITLE(state, title: TitleResponse) {
		state.title = title;
	},
	SET_SEASON(state, season: Title) {
		state.season = season;
	},
	SET_NEWEST_EPISODES(state, episodes: Episode[]) {
		state.newestEpisodes = (episodes || []).sort((a: Episode, b: Episode) => {
			return b.episode_number - a.episode_number;
		});
	},
	SET_SIMILAR_TITLES(state, similarTitles: Title[]) {
		state.similarTitles = similarTitles;
	},
	SET_SIMILAR_UPCOMING_TITLES(state, upcomingTitles: Title[]) {
		state.upcomingTitles = upcomingTitles;
	},
	SET_MODULE_CONTEXT(state, { module, moduleIndex }: { module: DiscoveryModule; moduleIndex: number }) {
		state.moduleContext = new SnowplowModuleContext(
			module?.id ?? '',
			moduleIndex ?? -1,
			module.template.technicalName,
			module.discoverySessionId ?? '',
			module.template?.anchor ?? undefined
		);
	},
	CLEAR_MODULE_CONTEXT(state) {
		state.moduleContext = null;
	},
	SET_TITLE_CONTEXT(
		state,
		[titleId, objectType, seasonNumber, episodeNumber, contentType]: SnowplowTitleContextGraphqlPayload
	) {
		state.titleContext = new SnowplowTitleContextGraphql(
			titleId,
			objectType,
			seasonNumber,
			episodeNumber,
			contentType
		);
	},
	CLEAR_TITLE_CONTEXT(state) {
		state.titleContext = null;
	},
	INIT_TITLE_LOADED_PROMISE(state) {
		let resolve: any;
		const promise = new Promise<void>(r => {
			resolve = r;
		});
		state._titleLoadedPromise = promise;
		state._titleLoadedResolve = resolve;
	},
	RESOLVE_TITLE_LOADED_PROMISE(state) {
		if (state._titleLoadedResolve) {
			state._titleLoadedResolve();
		}
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
} as Module<State, any>;
